import { CellClassParams, ColDef, GridOptions, ValueGetterParams } from 'ag-grid-community';
import { Component, ElementRef, HostListener, Input, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { GridTemplateComponent } from 'src/app/reusables/grid-template/grid-template.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { CxoneTeamsComponent } from '../../toolbar-items/cxone-teams/cxone-teams.component';
import { MetricDataValue, QMEvaluatorPerformanceData, ChartLegends, ComparisonChartColors, DisplayViewOption, LineChartDataValues } from './qm-evaluator-performance.model';
import { QMEvaluatorPerformanceService } from './qm-evaluator-performance.service';
import { AgGridAngular } from 'ag-grid-angular';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ServerSideOptions } from '@niceltd/cxone-components/singleselect-dropdown';
import { QmEvaluatorPerformanceComparisonComponent } from './qm-evaluator-performance-comparison/qm-evaluator-performance-comparison.component';

@Component({
    selector: 'cv-qm-evaluator-performance',
    templateUrl: './qm-evaluator-performance.component.html',
    styleUrls: ['./qm-evaluator-performance.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class QmEvaluatorPerformanceComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    @ViewChild('completedTemplate', { static: true }) completedTemplate: TemplateRef<any>;
    @ViewChild("searchBox", { read: ElementRef }) searchBox: ElementRef<HTMLInputElement>;
    @ViewChild('evaluatorPerformanceWidgetGrid') agGrid: AgGridAngular;
    @ViewChild("legendRef") legendRef: ElementRef;
    @ViewChild("chartContainer") chartContainer: ElementRef;
    @ViewChild('chart1') chart1: QmEvaluatorPerformanceComparisonComponent;
    @ViewChild('chart2') chart2: QmEvaluatorPerformanceComparisonComponent;
    @ViewChild('trendLineChart') trendLineChart: any;

    showFilter = false;
    filterValue = '';
    actualFilter = '';
    dateRangeSelected: any;

    // Trigger for the dropdown menu
    isOpen = false;

    // AG Grid
    gridOptions: GridOptions = null;
    data: QMEvaluatorPerformanceData;
    isDataLoading = false;
    isFirstLoad: boolean;
    noData = false;
    isCXOne = false;
    dataLoadSuccess = false;

    //For cxone search placeholder localization
    searchPlaceholder = "";

    // For toggle the display view
    toggleDisplayView: DisplayViewOption = 0;

    // display view dropdowns
    loading = false;
    dataLimit = -1;
    skip = 0;
    top = 50;
    agentsData = [];
    evaluatorDropdownPlaceholder = "";
    isComparisonDataPresent = false;
    evaluatorDropdownOptions = [];
    firstEvaluatorDropdown = undefined;
    secondEvaluatorDropdown = undefined;
    lineChartEvaluatorDropdown = undefined;
    serverSideOptions: ServerSideOptions = {
        keyStrokeDelay: 1000,
        resultsCount: 200,
        enabled: true
    };
    chartData1: MetricDataValue;
    chartData2: MetricDataValue;
    chartData3: any = {};
    chartLegends: ChartLegends[] = [
        { label: "Completed", key: "completed", color: ComparisonChartColors.completed, toggleLegend: false, className: '' },
        { label: "Pending", key: "pending", color: ComparisonChartColors.pending, toggleLegend: false, className: '' },
        { label: "Appealed", key: "appealed", color: ComparisonChartColors.appealed, toggleLegend: false, className: '' },
        { label: "Deleted", key: "deleted", color: ComparisonChartColors.deleted, toggleLegend: false, className: '' },
        { label: "Replaced", key: "replaced", color: ComparisonChartColors.replaced, toggleLegend: false, className: '' },
        { label: "Expired", key: "expired", color: ComparisonChartColors.expired, toggleLegend: false, className: '' },
        { label: "Appealed Expired", key: "appealedExpired", color: ComparisonChartColors.appealedExpired, toggleLegend: false, className: '' },
        { label: "Completed Deleted", key: "completedDeleted", color: ComparisonChartColors.completedDeleted, toggleLegend: false, className: '' }
    ];
    private gridState = null;
    private stateSaveTimer: number;
    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting };
    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"
    isResizingModule = true;
    showLegends = true;

    gridReady = false; // Used to only construct the ag-grid table when ready.
    displayViewOption = DisplayViewOption;
    chartObj: any;
    fetchChartObj: boolean;

    @HostListener('window:resize', ['$event'])
    onResize(e: any) {
        if (this.firstEvaluatorDropdown || this.secondEvaluatorDropdown) {
            this.handleModuleSize();
        }
    }

    constructor(
        private agGridHelperService: AgGridHelperService,
        public cvLocalizationService: CVLocalizationService,
        private service: QMEvaluatorPerformanceService,
        public config: CvConfigurationService,
    ) {
        this.isCXOne = config.isCxOne;
    }

    async ngOnInit(): Promise<void> {
        const localizationPromises = [
            this.cvLocalizationService.GetSection('Evaluator_Performance'),
            this.cvLocalizationService.GetSection("Error_Module"),
            this.cvLocalizationService.GetSection("Common"),
        ];
        await Promise.all(localizationPromises);
        this.moduleManager.setOptions({
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: () => this.loadData(false),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: isAutoRefresh => {
                if (!isAutoRefresh) {
                    this.moduleManager.showLoader = true;
                }
                this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading,
            onExport: (format, isCXOne) => this.service.getExport(this.moduleKey, format, isCXOne),
            onImageExport: () => this.exportPNG(),
            imageExportVisible: () => this.toggleDisplayView !== this.displayViewOption.TABLE,
        });
        this.evaluatorDropdownPlaceholder = this.cvLocalizationService.getCachedText("Evaluator_Performance", "Select_Evaluator", "Select Evaluator");
        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");
        this.moduleManager.getModuleInfo().DTO.SettingEmpSelector = false; // Hide the Emp Selector since there is a custom teams selector
        await this.setupGrid();
        this.moduleManager.addHeaderItem({
            name: 'Search',
            icon: 'search',
            click: () => this.toggleOpenSearch(),
        });
        this.moduleManager.addToolbarItem(CxoneTeamsComponent);
        this.moduleManager.addState({
            name: 'gridState',
            getStateData: () => this.gridState,
            onStateLoaded: (stateData) => {
                if (!stateData) {
                    return;
                }
                this.gridState = stateData;
                if (this.agGrid?.api) {
                    this.agGridHelperService.loadGridState(this.agGrid?.api, stateData);
                }
            },
            autoLoad: true
        });

        this.moduleManager.addSetting({
            name: 'ToggleDisplayView',
            getValue: () => this.toggleDisplayView,
            onSettingLoaded: value => {
                this.toggleDisplayView = value ? value : this.displayViewOption.TABLE;
            },
            defaultValue: 0,
        });
        this.moduleManager.addSetting({
            name: 'FirstEvaluatorDropdown',
            getValue: () => this.firstEvaluatorDropdown,
            onSettingLoaded: value => { this.firstEvaluatorDropdown = value; },
            defaultValue: undefined,
        });
        this.moduleManager.addSetting({
            name: 'SecondEvaluatorDropdown',
            getValue: () => this.secondEvaluatorDropdown,
            onSettingLoaded: value => { this.secondEvaluatorDropdown = value; },
            defaultValue: undefined,
        });
        this.moduleManager.addSetting({
            name: 'LineChartEvaluatorDropdown',
            getValue: () => this.lineChartEvaluatorDropdown,
            onSettingLoaded: value => { this.lineChartEvaluatorDropdown = value; },
            defaultValue: undefined,
        });
        this.moduleManager.addSetting({
            name: 'FilterValue',
            getValue: () => this.filterValue,
            onSettingLoaded: value => { this.filterValue = value; this.applyFilter() },
            defaultValue: '',
        });
        this.moduleManager.customSettingsAdded = true;
        this.moduleManager.registerEvents({
            onResizeStop: (() => {
                this.showLegends = false;
                this.handleModuleSize();
                window.setTimeout(() => {
                    this.showLegends = true;
                }, 300);//setTimeout is required here, because of dynamically assigning the font size and min-width for the legends based on center label of chart
            })
        });

        if (this.isCXOne) {
            this.loadCXOneConfigurations();
        }
    }

    applyFilter() {
        if (this.agGrid?.api) {
            this.actualFilter = this.filterValue;
            this.actualFilter = this.actualFilter.trim();
            this.agGrid?.api.setGridOption('quickFilterText', this.actualFilter);
            if (this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    clearFilter() {
        this.filterValue = '';
        this.applyFilter();
    }

    private async setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                minWidth: this.agGridHelperService.defaultMinWidth,
            },
            getRowId: ({ data: rowData }) => rowData.ID,
            overlayNoRowsTemplate: this.isCXOne ?
                `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>` : null,
            onGridReady: event => {
                this.gridReady = true;
                if (this.data) {
                    this.agGrid?.api.setGridOption('rowData', this.data.DataRows);
                }
                this.stateRestoreInProgress = true; // Wait until the widget is "stable" before letting publish bars show while restoring state
                this.restoreTableState(); //Needed to restore columns size saved on the gridState.
                if (!this.gridState) {
                    this.agGridHelperService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the gridState is null.
                    this.queueStateSave();
                }

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save
                this.constructColumnMenuItem();
            },
            onSortChanged: () => this.queueStateSave(),
            onColumnMoved: () => this.queueStateSave(),
            onColumnPinned: () => this.queueStateSave(),
            onColumnVisible: () => this.queueStateSave(),
            onColumnResized: event => {
                if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                    this.queueStateSave();
                }
            },

            onFilterChanged: () => this.queueStateSave(),
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.queueStateSave();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreTableState();
                }
                this.constructColumnMenuItem();
            },
            localeText: await this.agGridHelperService.getLocalizations(),
            suppressDragLeaveHidesColumns: true,
            suppressScrollOnNewData: true,
            suppressNoRowsOverlay: true,
        });
        this.gridOptions.columnDefs = [];
        const name = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Name', 'Name');
        const completed = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Completed', 'Completed');
        const appealed = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Appealed', 'Appealed');
        const Replaced = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Replaced', 'Replaced');
        const Deleted = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Deleted', 'Deleted');
        const Expired = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Expired', 'Expired');
        const avgScore = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Avg_Score', 'Avg Score');
        const totalEvaluations = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Total_Evaluations', 'Total Evaluations');
        const appealedExpired = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'appealedExpired', 'Appealed Expired');
        const completedDeleted = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'completedDeleted', 'Completed Deleted');
        this.gridOptions.columnDefs.push({
            headerName: name,
            field: 'Name',
            colId: 'Name',
            valueGetter: (node: ValueGetterParams) => node.data?.Name ?? '',
            filterValueGetter: (node: ValueGetterParams) => node.data?.Name ?? '',
            filter: 'agTextColumnFilter',
        });
        this.gridOptions.columnDefs.push({
            headerName: completed + ' %',
            field: 'Completed',
            colId: 'Completed',
            valueGetter: (node: ValueGetterParams) => node.data?.Completed.CompletedDisplayValue,
            comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                if (nodeA === undefined || nodeB === undefined) return;
                if (nodeA.data.Completed.CompletedValue === nodeB.data.Completed.CompletedValue) return 0;
                return (nodeA.data.Completed.CompletedValue > nodeB.data.Completed.CompletedValue) ? 1 : -1;
            },
            cellStyle: (node: CellClassParams) => {
                if (node.data?.Completed.CompletedDisplayValue === 'N/A') return;
                if (node.data?.Completed.MetricDatas.Total.MetricValue === 0) return;
                const completedScale = this.moduleManager.customSettings.CompletedScale.getValue();
                if (node.data?.Completed.CompletedValue <= completedScale[0])
                    return { backgroundColor: '#E0775F' };
                if (node.data?.Completed.CompletedValue >= completedScale[1])
                    return { backgroundColor: '#579FCA' };
            },
            cellRenderer: GridTemplateComponent,
            cellRendererParams: {
                ngTemplate: this.completedTemplate
            },
            filterValueGetter: (node: ValueGetterParams) => node.data?.Completed.CompletedValue,
            filter: 'agNumberColumnFilter',
        });

        this.gridOptions.columnDefs.push({
            headerName: avgScore,
            field: 'Avg_Score',
            colId: 'AvgScore',
            valueGetter: (node: ValueGetterParams) => node.data?.Completed.MetricDatas.AvgScore.MetricDisplayValue === null ? 0 : node.data?.Completed.MetricDatas.AvgScore.MetricDisplayValue,
            hide: true,
        });

        this.gridOptions.columnDefs.push({
            headerName: appealed + ' %',
            field: 'Appealed',
            colId: 'Appealed',
            valueGetter: (node: ValueGetterParams) => node.data?.Appealed.AppealedDisplayValue,
            cellStyle: (node: CellClassParams) => {
                if (node.data?.Appealed?.AppealedMetricData?.MetricValue > this.moduleManager.customSettings.AppealedScale.getValue() / 100)
                    return { backgroundColor: '#E0775F' };
            },
            filterValueGetter: (node: ValueGetterParams) => node.data?.Appealed?.AppealedMetricData?.Value ?? 0,
            filter: 'agNumberColumnFilter',
        });

        this.gridOptions.columnDefs.push({
            headerName: totalEvaluations,
            field: 'Total_Evaluations',
            colId: 'TotalEvaluations',
            valueGetter: (node: ValueGetterParams) => node.data?.Completed.MetricDatas.Total.MetricValue,
            hide: true,
        });

        this.gridOptions.columnDefs.push({
            headerName: Deleted,
            field: 'Deleted',
            colId: 'Deleted',
            valueGetter: (node: ValueGetterParams) => node.data?.Completed.MetricDatas.Deleted.MetricValue,
            hide: true,
        });

        this.gridOptions.columnDefs.push({
            headerName: Replaced,
            field: 'Replaced',
            colId: 'Replaced',
            valueGetter: (node: ValueGetterParams) => node.data?.Completed.MetricDatas.Replaced.MetricValue,
            filter: 'agTextColumnFilter',
            hide: true,
        });

        this.gridOptions.columnDefs.push({
            headerName: Expired,
            field: 'Expired',
            colId: 'Expired',
            valueGetter: (node: ValueGetterParams) => node.data?.Completed.MetricDatas.Expired.MetricValue,
            filter: 'agTextColumnFilter',
            hide: true,
        });

        this.gridOptions.columnDefs.push({
            headerName: appealedExpired,
            field: 'Appealed_Expired',
            colId: 'AppealedExpired',
            valueGetter: (node: ValueGetterParams) => node.data?.Completed.MetricDatas.AppealedExpired.MetricValue,
            filter: 'agTextColumnFilter',
            hide: true,
        });

        this.gridOptions.columnDefs.push({
            headerName: completedDeleted,
            field: 'Completed_Deleted',
            colId: 'CompletedDeleted',
            valueGetter: (node: ValueGetterParams) => node.data?.Completed.MetricDatas.CompletedDeleted.MetricValue,
            filter: 'agTextColumnFilter',
            hide: true,
        });

        this.agGrid?.api?.setGridOption('columnDefs', []);
        this.agGrid?.api?.setGridOption('columnDefs', this.gridOptions.columnDefs);
        this.agGrid?.api?.sizeColumnsToFit();
    }

    private restoreTableState() {
        if (this.gridState && this.gridState.columns && this.agGrid?.api) {
            this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
        }
    };

    private async loadData(isAutoRefresh: boolean) {
        if (this.isDataLoading) {
            return; // don't have multiple outgoing requests
        }
        this.isDataLoading = true;
        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }
        try {
            this.data = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            this.noData = this.data.DataRows.length === 0;
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data.DataRows);
                this.agGrid?.api.redrawRows();
            }
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            this.dataLoadSuccess = !this.noData;
            this.moduleManager.showLoader = false;
            this.isDataLoading = false;
            this.moduleManager.showLoader = false;
            this.setAgentsData();
            this.validateDropdowns();
            this.handleViewModes(this.toggleDisplayView);
        }
    }

    private validateDropdowns() {
        // if agentsData is empty, remove all dropdown selections
        if (!this.agentsData.length) {
            this.firstEvaluatorDropdown = undefined;
            this.secondEvaluatorDropdown = undefined;
            this.lineChartEvaluatorDropdown = undefined;
            return;
        }

        // if this.lineEvaluatorDropdown is not in the list of agents, reset it to the first agent
        if (this.lineChartEvaluatorDropdown && !this.agentsData.find(a => a.id === this.lineChartEvaluatorDropdown.id)) {
            this.lineChartEvaluatorDropdown = this.agentsData[0];
        }

        // if this.firstEvaluatorDropdown is not in the list of agents, reset it to the first agent
        if (this.firstEvaluatorDropdown && !this.agentsData.find(a => a.id === this.firstEvaluatorDropdown.id)) {
            this.firstEvaluatorDropdown = this.agentsData[0];
        }

        // if this.secondEvaluatorDropdown is not in the list of agents, reset it to the first agent
        if (this.secondEvaluatorDropdown && !this.agentsData.find(a => a.id === this.secondEvaluatorDropdown.id)) {
            this.secondEvaluatorDropdown = this.agentsData[0];
        }
    }

    private queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }
        // Create a timer to save the state 500ms from now.
        this.stateSaveTimer = window.setTimeout(async () => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api) {
                this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
            }
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    }

    private toggleOpenSearch() {
        if (this.showFilter) {
            this.showFilter = false;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.showFilter = true;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    }

    private async focusOnSearch() {
        //Change input focus to the search bar.
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.config.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            } else {
                this.searchBox?.nativeElement?.focus();
            }
        }, 200);
    }

    /**
     * Function to load the CXOne configurations
     */
    public loadCXOneConfigurations() {
        const displayItems = [];
        displayItems.push({
            name: this.cvLocalizationService.getCachedText("Evaluator_Performance", "Table", "Table"),
            icon: '',
            click: () => this.handleDisplayView(this.displayViewOption.TABLE)
        });
        displayItems.push({
            name: this.cvLocalizationService.getCachedText("Evaluator_Performance", "Completed_Evaluations_Trend", "Completed Evaluations Trend"),
            icon: '',
            click: () => this.handleDisplayView(this.displayViewOption.COMPLETED_EVALUATIONS_TREND)
        });
        displayItems.push({
            name: this.cvLocalizationService.getCachedText("Evaluator_Performance", "Comparison", "Comparison"),
            icon: '',
            click: () => this.handleDisplayView(this.displayViewOption.COMPARISON)
        });

        this.moduleManager.addMenuItem({
            name: 'Widget Display',
            icon: 'chartType',
            type: 'subMenu',
            getEnabled: () => true,
            subItems: displayItems,
            readOnlyEnabled: this.isCXOne
        } as ModuleManagerSubMenu);
    }

    handleViewModes(mode: DisplayViewOption) {
        this.toggleDisplayView = mode;
        switch (mode) {
            case this.displayViewOption.TABLE:
                this.CXOneCallTableView();
                break;
            case this.displayViewOption.COMPLETED_EVALUATIONS_TREND:
                this.handleChartWithDropdowns();
                this.selection3Changed({ selected: this.lineChartEvaluatorDropdown }, false);
                break;
            case this.displayViewOption.COMPARISON:
                this.handleChartWithDropdowns();
                this.selection1Changed({ selected: this.firstEvaluatorDropdown }, false);
                this.selection2Changed({ selected: this.secondEvaluatorDropdown }, false);
                break;
        }
    }

    requestPublishNeeded() {
        this.moduleManager.requestPublishNeeded();
    }

    constructColumnMenuItem() {
        if (this.agGrid?.api)
            this.addColumnMenuItem();
        else
            console.error("constructColumnMenuItem() is being called outside of gridOptions");
    };

    addColumnMenuItem() {
        this.moduleManager.addMenuItem({
            name: "Columns",
            icon: "columns",
            getEnabled: () => this.gridOptions.columnDefs.length > 0 && this.toggleDisplayView === this.displayViewOption.TABLE,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.gridOptions.columnDefs,
            getItemName: i => {
                const colState = this.gridOptions.columnDefs[i];
                const column = this.agGrid?.api.getColumn(colState['colId']);

                if (column) // during column changes the state can have columns that are no longer in the list
                    return column['colDef'].headerName;
                else
                    return "";
            },
            isItemChecked: i => {
                if (this.gridReady && !this.gridState) {
                    this.prepareTableState();
                }
                const columnDefinition: ColDef = this.gridOptions.columnDefs[i];

                // Since the index values may be out of order if columns are reordered,
                // find the correct table state by name/field instead of relying on the index.
                const tableStateMatch = this.gridState.columns.find(c => c.name === columnDefinition.colId);
                if (tableStateMatch) {
                    columnDefinition.hide = !tableStateMatch.visible;
                }
                return !columnDefinition.hide;
            },
            onCheckClicked: i => this.toggleColumnVisible(i),
        } as IModuleManagerMenuItem);
    }

    // Function called when the menu item is checked/unchecked to hide/show columns.
    toggleColumnVisible(i) {
        const column: ColDef = this.gridOptions.columnDefs[i]; //column clicked
        const tableStateMatch = this.gridState.columns.find(c => c.name === column.colId); //column on table
        if (tableStateMatch) {
            column.hide = !tableStateMatch.visible;
        }
        const isHidden = !tableStateMatch.visible;

        // toggle column visibility
        this.agGrid?.api.setColumnVisible(tableStateMatch.name, isHidden);

        // update table state immediately to prevent UI delay (instead of queuing)
        this.prepareTableState();
        this.queueStateSave();
    }

    prepareTableState() {
        if (!this.agGrid?.api)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid.api);
        if (tableState != null) {
            this.gridState = tableState;
        }
    }

    /**
     * Function to call the Table View
     */
    private CXOneCallTableView() {
        this.moduleManager.addHeaderItem({
            name: 'Search',
            icon: 'search',
            click: () => this.toggleOpenSearch(),
        });
        if (this.filterValue) {
            this.showFilter = true;
            this.applyFilter();
        }
    }

    /**
     * Function for handle dropdown and chart in 
     * Comparison View and Completed Evaluations Trend
     */
    handleChartWithDropdowns() {
        const element = document.querySelector(`#Module-${this.moduleKey}-search`);
        if (element) {
            element.remove();
            this.showFilter = false;
        }
        if (!this.evaluatorDropdownOptions.length) {
            this.setDropdownOptions();
        }
        this.moduleManager.showLoader = false;
        this.isDataLoading = false;
        this.onCloseDropdown();
    }

    private setAgentsData() {
        let data = this.data?.DataRows.map((item: any) => ({ label: item.Name, id: item.ID }));
        this.agentsData = data || [];
        this.skip = 0;
        this.evaluatorDropdownOptions = [...this.agentsData?.slice(this.skip, this.skip + this.top)];
        const firstselectItem = this.agentsData.filter(agent => agent.id == this.firstEvaluatorDropdown?.id);
        if (firstselectItem.length === 0) {
            this.firstEvaluatorDropdown = undefined;
        }
        const secondSelectItem = this.agentsData.filter(agent => agent.id == this.secondEvaluatorDropdown?.id);
        if (secondSelectItem.length === 0) {
            this.secondEvaluatorDropdown = undefined;
        }
    }

    private setDropdownOptions() {
        this.loading = true;
        this.evaluatorDropdownOptions = [...this.agentsData.slice(this.skip, this.skip + this.top)];
        this.skip = this.skip + this.top;
        this.dataLimit = [...this.agentsData].length;
        this.loading = false;
    }

    onLoadData() {
        if (this.skip <= this.dataLimit) {
            this.loading = true;
            this.evaluatorDropdownOptions = this.evaluatorDropdownOptions.concat(this.agentsData.slice(this.skip, this.skip + this.top));
            this.skip = this.skip + this.top;
            this.loading = false;
        }
    }

    selection1Changed(val: any, isFromDropdown = true): void {
        this.chartData1 = this.getFilteredData(val);
        if (isFromDropdown) {
            this.saveSettings();
            this.requestPublishNeeded();
        }
    }

    selection2Changed(val: any, isFromDropdown = true): void {
        this.chartData2 = this.getFilteredData(val);
        if (isFromDropdown) {
            this.saveSettings();
            this.requestPublishNeeded();
        }
    }

    async selection3Changed(val: any, isFromDropdown = true) {
        if (val.selected) {
            this.moduleManager.showLoader = true;
            let filterableId = val.selected?.id;
            let lineChartData: LineChartDataValues = await this.service.loadLineChartData(this.moduleKey, filterableId);
            this.chartData3 = { ...lineChartData };
            if (isFromDropdown) {
                this.saveSettings();
                this.requestPublishNeeded();
            }
            this.moduleManager.showLoader = false;
        }
    }

    getFilteredData(val: any): MetricDataValue {
        let keyValue = val.selected?.id;
        let metricData: any = this.data.DataRows.find(obj => obj.ID === keyValue);
        return {
            "total": metricData ? metricData.Completed.MetricDatas.Total.MetricValue : 0,
            "completed": metricData ? metricData.Completed.MetricDatas.Completed.MetricValue : 0,
            "pending": metricData ? metricData.Completed.MetricDatas.Pending.MetricValue : 0,
            "expired": metricData ? metricData.Completed.MetricDatas.Expired.MetricValue : 0,
            "deleted": metricData ? metricData.Completed.MetricDatas.Deleted.MetricValue : 0,
            "replaced": metricData ? metricData.Completed.MetricDatas.Replaced.MetricValue : 0,
            "appealed": metricData ? metricData.Completed.MetricDatas.Appealed.MetricValue : 0,
            "appealedExpired": metricData ? metricData.Completed.MetricDatas.AppealedExpired.MetricValue : 0,
            "completedDeleted": metricData ? metricData.Completed.MetricDatas.CompletedDeleted.MetricValue : 0
        };
    }

    toggleLegend(item) {
        item.toggleLegend = !item.toggleLegend;
        this.chartLegends = [...this.chartLegends];
    }

    handleModuleSize() {
        this.isResizingModule = false;
        window.requestAnimationFrame(async () => {
            await this.delay(0);
            this.isResizingModule = true;
        });
    }

    delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    onSearch(event) {
        this.loading = true;
        this.skip = 0;
        this.top = 50;
        const parsedResults = this.agentsData;
        let options = [];

        if (this.isEmpty(event.query)) {
            options = parsedResults;
        } else {
            options = parsedResults.filter((item) => {
                return item.label.toLowerCase().search(event.query.toLowerCase()) !== -1;
            });
        }

        this.serverSideOptions.resultsCount = options.length;
        this.evaluatorDropdownOptions = [...options.slice(this.skip, this.skip + this.top)];
        this.skip = this.skip + this.top;
        this.dataLimit = options.length;
        this.loading = false;
    }

    private isEmpty(value) {
        return value === '' || value === undefined || value === null;
    }

    onCloseDropdown() {
        this.skip = 0;
        this.top = 50;
        this.evaluatorDropdownOptions = [...this.agentsData.slice(this.skip, this.skip + this.top)];
        this.skip = this.skip + this.top;
        this.dataLimit = this.agentsData.length;
    }

    saveSettings() {
        this.moduleManager.saveSettings();
    }

    /**Function to export the widget information to PNG */
    exportPNG() {
        if (this.trendLineChart?.chartObject) {
            const date = (new Date()).toString('yyyy_MM_dd_HH_mm_ss');
            const chartName = this.cvLocalizationService.getCachedText("Evaluator_Performance", "Completed_Evaluations_Trend", "Completed Evaluations Trend");
            const fileName = this.cvLocalizationService.getCachedText("Module_Names", "CXone_QmEvaluatorPerformance", "Evaluator Performance")
                .replace(/\s+/g, '-').toLowerCase() + '_widget_(' + chartName + ')_(' + date + ').png';

            const link = document.createElement('a');
            link.setAttribute('download', fileName);
            link.setAttribute('href', this.trendLineChart.chartObject.chart.toBase64Image());
            link.click();

            URL.revokeObjectURL(link.href);
            link.remove();
        }
        if (this.chart1?.chartObject || this.chart2?.chartObject) {
            this.downloadCombinedChart();
        }
    }

    setChartObj(event: any) {
        this.chartObj = event;
    }

    downloadCombinedChart() {
        const canvas1 = this.chart1?.chartObject.chart.canvas;
        const canvas2 = this.chart2?.chartObject.chart.canvas;
        const legendHeight = 20;
        const combinedCanvas = document.createElement('canvas');
        const context = combinedCanvas.getContext('2d');
        const extraCanvasWidth = canvas1.width < 186 ? (200 + (186 - canvas1.width)) : 200;
        const extraCanvasHeight = 50;
        let canvas1Height = canvas1 ? canvas1.height + extraCanvasHeight : 0;
        let canvas2Height = canvas2 ? canvas2.height + extraCanvasHeight : 0;
        let canvas1Width = canvas1 ? canvas1.width + extraCanvasWidth : canvas2?.width + extraCanvasWidth;
        let canvas2Width = canvas2 ? canvas2.width + extraCanvasWidth : canvas1?.width + extraCanvasWidth;
        combinedCanvas.width = canvas1Width + canvas2Width;
        combinedCanvas.height = Math.max(canvas1Height, canvas2Height) + legendHeight;
        context.fillStyle = 'white';
        context.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);
        if (canvas1) {
            context.drawImage(canvas1, 100, 0);
        }
        if (canvas2) {
            let dx = canvas1 ? canvas1.width + 300 : canvas2.width + 300;
            // Draw the second chart to the right of the first chart
            context.drawImage(canvas2, dx, 0);
        }
        // Calculate the starting x-coordinate for center alignment or left margin
        const startX = 30;
        // Draw the legends below the charts
        let currentX = startX;
        this.chartLegends.forEach((legend, ind) => {

            const color = legend.color
            const text = legend.label;
            const fontSize = 12;
            let height = canvas1Height !== 0 ? canvas1Height : canvas2Height;

            // Draw the color box
            context.fillStyle = color;
            context.fillRect(currentX, height, fontSize, fontSize);

            // Draw the text
            context.fillStyle = 'black';
            context.font = `${fontSize}px Open Sans`;
            if (legend.toggleLegend) {
                const textWidth = context.measureText(text).width;
                context.strokeStyle = 'black';
                context.lineWidth = 1;
                context.beginPath();
                context.moveTo(currentX + fontSize + 5, canvas1.height + fontSize / 2);
                context.lineTo(currentX + fontSize + 5 + textWidth, canvas1.height + fontSize / 2);
                context.stroke();
            }
            context.fillText(text, currentX + fontSize + 5, height + fontSize);
            // Update the current x position for the next legend
            currentX += fontSize + 5 + context.measureText(text).width + 10; // Adding 20 for spacing between legends
        });
        const date = (new Date()).toString('yyyy_MM_dd_HH_mm_ss');
        const chartName = this.cvLocalizationService.getCachedText("Evaluator_Performance", "Comparison", "Comparison");
        const fileName = this.cvLocalizationService.getCachedText("Module_Names", "CXone_QmEvaluatorPerformance", "Evaluator Performance")
            .replace(/\s+/g, '-').toLowerCase() + '_widget_(' + chartName + ')_(' + date + ').png';
        // Create a link to download the image
        const link = document.createElement('a');
        link.href = combinedCanvas.toDataURL('image/png');
        link.download = fileName;
        link.click();
    }

    handleDisplayView(view: DisplayViewOption) {
        this.requestPublishNeeded();
        this.handleViewModes(view);
        this.saveSettings();
    }
}
